import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../../api/apiServices";

const THEMES  = ["emerald","blue","purple","orange","rose","amber"];
const ICONS   = ["ri-gift-line","ri-flashlight-line","ri-wifi-line","ri-phone-line","ri-tv-2-line","ri-bank-card-line","ri-coin-line","ri-hand-coin-line","ri-share-line","ri-calendar-check-line","ri-trophy-line","ri-fire-line","ri-rocket-line","ri-heart-line","ri-star-line"];
const PAGES   = ["home","airtime","data","electricity","tv"];

const THEME_BG = { emerald:"#065F46", blue:"#1E3A5F", purple:"#3B0764", orange:"#7C2D12", rose:"#881337", amber:"#78350F" };
const THEME_ACC = { emerald:"#059669", blue:"#0284C7", purple:"#7C3AED", orange:"#EA580C", rose:"#E11D48", amber:"#D97706" };

function SlidePreview({ slide }) {
  const bg  = THEME_BG[slide.theme]  || "#065F46";
  const acc = THEME_ACC[slide.theme] || "#059669";
  return (
    <div className="rounded-xl overflow-hidden h-[80px] relative" style={{ background: `linear-gradient(135deg, ${bg} 0%, ${acc} 100%)` }}>
      <div className="absolute inset-0 flex items-center gap-3 px-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,255,255,0.15)" }}>
          <i className={`${slide.icon || "ri-gift-line"} text-white text-lg`} />
        </div>
        <div>
          <p className="text-white text-xs font-bold leading-tight">{slide.title || "Slide title"}</p>
          {slide.subtitle && <p className="text-white/60 text-[10px] mt-0.5 line-clamp-1">{slide.subtitle}</p>}
          {slide.ctaText && (
            <span className="inline-block mt-1 text-[9px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: "rgba(255,255,255,0.2)" }}>
              {slide.ctaText} →
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SlideModal({ slide, onClose, onSave }) {
  const isNew = !slide?._id;
  const [form, setForm] = useState({
    title:     slide?.title     || "",
    subtitle:  slide?.subtitle  || "",
    ctaText:   slide?.ctaText   || "",
    ctaLink:   slide?.ctaLink   || "",
    theme:     slide?.theme     || "emerald",
    icon:      slide?.icon      || "ri-gift-line",
    pages:     slide?.pages     || ["home"],
    sortOrder: slide?.sortOrder ?? 0,
    isActive:  slide?.isActive  ?? true,
    expiresAt: slide?.expiresAt ? new Date(slide.expiresAt).toISOString().slice(0, 16) : "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const togglePage = (p) => set("pages", form.pages.includes(p) ? form.pages.filter(x => x !== p) : [...form.pages, p]);

  const handleSave = async () => {
    if (!form.title) return;
    setSaving(true);
    try {
      await onSave({ ...form, expiresAt: form.expiresAt || null });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#161616] rounded-2xl border border-white/10 w-full max-w-md shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <p className="text-white font-bold text-sm">{isNew ? "New Slide" : "Edit Slide"}</p>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/50 text-xs" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Live preview */}
          <SlidePreview slide={form} />

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Title *</label>
            <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Check in daily to get ₦1,000"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Subtitle</label>
            <input value={form.subtitle} onChange={e => set("subtitle", e.target.value)} placeholder="Open the app every day to earn rewards"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">CTA Button Text</label>
              <input value={form.ctaText} onChange={e => set("ctaText", e.target.value)} placeholder="Learn More"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none" />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">CTA Link</label>
              <input value={form.ctaLink} onChange={e => set("ctaLink", e.target.value)} placeholder="/rewards"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none" />
            </div>
          </div>

          {/* Theme */}
          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-2">Theme</label>
            <div className="flex gap-2">
              {THEMES.map(t => (
                <button key={t} onClick={() => set("theme", t)} title={t}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${form.theme === t ? "border-white scale-110" : "border-transparent"}`}
                  style={{ background: `linear-gradient(135deg, ${THEME_BG[t]}, ${THEME_ACC[t]})` }} />
              ))}
            </div>
          </div>

          {/* Icon picker */}
          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-2">Icon</label>
            <div className="flex flex-wrap gap-2">
              {ICONS.map(ic => (
                <button key={ic} onClick={() => set("icon", ic)}
                  className={`w-9 h-9 rounded-xl flex items-center justify-center border transition-all ${form.icon === ic ? "border-[#00C896] bg-[#00C896]/15" : "border-white/10 bg-white/5 hover:bg-white/10"}`}>
                  <i className={`${ic} text-base ${form.icon === ic ? "text-[#00C896]" : "text-white/50"}`} />
                </button>
              ))}
            </div>
          </div>

          {/* Show on pages */}
          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-2">Show on Pages</label>
            <div className="flex flex-wrap gap-2">
              {PAGES.map(p => (
                <button key={p} onClick={() => togglePage(p)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-xl border transition-all capitalize ${
                    form.pages.includes(p) ? "bg-[#00C896]/15 border-[#00C896]/40 text-[#00C896]" : "bg-white/5 border-white/10 text-white/40"
                  }`}>{p}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Sort Order</label>
              <input type="number" value={form.sortOrder} onChange={e => set("sortOrder", Number(e.target.value))}
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none" />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Expires At</label>
              <input type="datetime-local" value={form.expiresAt} onChange={e => set("expiresAt", e.target.value)}
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none [color-scheme:dark]" />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-white text-sm font-medium">Active</p>
            <button onClick={() => set("isActive", !form.isActive)}
              className={`w-11 h-6 rounded-full transition-all relative ${form.isActive ? "bg-[#00C896]" : "bg-white/10"}`}>
              <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${form.isActive ? "left-6" : "left-1"}`} />
            </button>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/5 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.title} className="flex-1 py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] disabled:opacity-50">
            {saving ? "Saving…" : isNew ? "Create Slide" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MarketingSlidesView() {
  const [slides,    setSlides]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [editSlide, setEditSlide] = useState(null);

  const fetchSlides = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/mtrade/admin/marketing/slides");
      if (r.data.status) setSlides(r.data.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSlides(); }, []);

  const handleSave = async (form) => {
    if (editSlide._id) {
      await apiClient.patch(`/mtrade/admin/marketing/slides/${editSlide._id}`, form);
    } else {
      await apiClient.post("/mtrade/admin/marketing/slides", form);
    }
    fetchSlides();
  };

  const handleToggle = async (s) => {
    await apiClient.patch(`/mtrade/admin/marketing/slides/${s._id}`, { isActive: !s.isActive });
    setSlides(prev => prev.map(x => x._id === s._id ? { ...x, isActive: !x.isActive } : x));
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this slide?")) return;
    await apiClient.delete(`/mtrade/admin/marketing/slides/${id}`);
    setSlides(prev => prev.filter(s => s._id !== id));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-white">Marketing Slides</h2>
          <p className="text-white/40 text-xs mt-0.5">Promotional banners shown on home page and bill pages</p>
        </div>
        <button onClick={() => setEditSlide({})}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#00C896]/15 border border-[#00C896]/30 rounded-xl text-xs text-[#00C896] font-semibold hover:bg-[#00C896]/25">
          <i className="ri-add-line" /> New Slide
        </button>
      </div>

      <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="p-5 space-y-3">
            {[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />)}
          </div>
        ) : slides.length === 0 ? (
          <div className="py-14 text-center">
            <i className="ri-layout-masonry-line text-white/15 text-4xl mb-2 block" />
            <p className="text-white/25 text-sm">No slides yet</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {slides.map(s => (
              <div key={s._id} className={`px-4 py-3 transition-colors group ${!s.isActive ? "opacity-40" : "hover:bg-white/3"}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1"><SlidePreview slide={s} /></div>
                  <div className="flex flex-col gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100">
                    <button onClick={() => setEditSlide(s)} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center">
                      <i className="ri-edit-line text-white/50 text-xs" />
                    </button>
                    <button onClick={() => handleToggle(s)} className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center">
                      <i className={`text-xs ${s.isActive ? "ri-eye-off-line text-red-400/60" : "ri-eye-line text-[#00C896]/60"}`} />
                    </button>
                    <button onClick={() => handleDelete(s._id)} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-red-500/20 flex items-center justify-center">
                      <i className="ri-delete-bin-line text-red-400/40 text-xs" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {s.pages?.map(p => (
                    <span key={p} className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-white/8 text-white/40 capitalize">{p}</span>
                  ))}
                  {s.expiresAt && (
                    <span className="text-[9px] text-white/20">
                      expires {new Date(s.expiresAt).toLocaleDateString("en-NG", { day: "numeric", month: "short" })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editSlide !== null && (
        <SlideModal slide={editSlide} onClose={() => setEditSlide(null)} onSave={handleSave} />
      )}
    </div>
  );
}
